import { Circle, CircleOutlined } from "@mui/icons-material";
import { Box, List, ListItem, Stack, SvgIconProps, Typography } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { RandomTextSkeleton } from "../../App/Components/Skeleton/RandomTextSkeleton";
import { useCurrentUser } from "../../App/Queries/UserQueries";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { IGroup } from "../../App/Typings/Groups";
import { GroupChip } from "../../CandidateGroups/Components/DisplayGroupList/DisplayGroupList";
import { useGroupProfileContext } from "../State/GroupProfileContext";

export interface GroupDetail {
  title: string;
  key: string;
  data: string | number | ReactNode;
  tooltip?: string;
}

export const DisplayGroupDetails = () => {
  const { id: userId } = useCurrentUser();
  const { getUserAdjustedDate } = useUserProfileStore();
  const { group, isLoading } = useGroupProfileContext();
  const groupDetails: GroupDetail[] = useMemo(
    () => [
      {
        title: "Group Type:",
        key: "type",
        data: <GroupChip group={group ?? ({} as IGroup)} chipSx={{ maxWidth: "auto" }} />,
      },
      {
        title: "Candidates:",
        key: "members",
        data: group?.memberCount ?? 0,
      },
      {
        title: "Created By:",
        key: "created-by",
        data: group ? group.creatorNickname + (userId === group.creatorId ? " (You)" : "") : "",
      },
      {
        title: "Created Date:",
        key: "created-date",
        data: group ? getUserAdjustedDate(group.createdDate).format("MM/DD/YYYY") : "",
        tooltip: group ? getUserAdjustedDate(group.createdDate).format("MM/DD/YYYY h:mm A") : "",
      },
      {
        title: "Last Updated:",
        key: "last-updated",
        data: group ? getUserAdjustedDate(group.lastUpdateDate).format("MM/DD/YYYY") : "",
        tooltip: group ? getUserAdjustedDate(group.lastUpdateDate).format("MM/DD/YYYY h:mm A") : "",
      },
      {
        title: "Status:",
        key: "status",
        data: getGroupStatusDisplay(group),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group, userId],
  );
  return (
    <Stack direction="row" justifyContent="flex-start" sx={{ width: "100%" }}>
      {isLoading ? (
        <RandomTextSkeleton />
      ) : (
        <List
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            p: 0,
            gap: { xs: 1, sm: 4 },
          }}
          data-test="candidategroup-information"
        >
          {groupDetails &&
            groupDetails.map((data) => (
              <ListItem
                key={data.key}
                sx={{
                  width: "auto",
                  flex: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  p: 0,
                }}
              >
                <Typography sx={{ fontWeight: "bold", mb: "0.2em" }}>{data.title}</Typography>
                <Typography
                  component="div"
                  data-test={`group-details-value-${data.key}`}
                  title={data.tooltip}
                >
                  {data.data}
                </Typography>
              </ListItem>
            ))}
        </List>
      )}
    </Stack>
  );
};

function getGroupStatusDisplay(group?: IGroup) {
  const disabled = !group || group.isArchived;
  let activeStatus = group?.isArchived ? "Archived" : "Active";
  let iconColor = "primary" as SvgIconProps["color"];
  if (disabled) {
    iconColor = "disabled";
  }
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {activeStatus}
      {disabled ? (
        <CircleOutlined color={iconColor} fontSize="small" />
      ) : (
        <Circle color={iconColor} fontSize="small" />
      )}
    </Box>
  );
}
